import { HashrateItem } from '@components/Charts/ChartDatasetUtils';
import { MODALS_ENUM } from "@constants/Modals";
import { AddBuilding_RequestTypes, DeleteBuilding_RequestTypes, EditLocation_RequestTypes, GetObjectList_RequestTypes, GetStatisticByLocation_RequestTypes } from "@mobx/api/locations";
import apiService, { createStore, RequestControl, withLoading } from "@mobx/services/http";
import store_ from "@store";
import { entries } from "@utils/entries";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import moment, { Moment } from "moment";
import { show } from "redux-modal";
import { APIError } from 'wl-net';
import { createBuildingStore } from "./building";
import { BuildingStore, ILocationStore, ILocationStoreBase, IMainLocationsStore } from "./interfaces";
import { IBuilding, ILocation3, ILocationStringsData } from "./types";

const createLocationStore_ = (rc: RequestControl, location: ILocation3, mainStore: IMainLocationsStore): ILocationStore => {
  //const getObjects = apiService.createRequest(GetObjectList_RequestTypes);
  const deleteBuilding = apiService.createRequest(DeleteBuilding_RequestTypes);
  const addBuilding = apiService.createRequest(AddBuilding_RequestTypes);
  const getStatistics = apiService.createRequest(GetStatisticByLocation_RequestTypes);
  const edit = apiService.createRequest(EditLocation_RequestTypes);

  let activeBuildingId: number | undefined = undefined;

  const load = async () => {
    const buildings: Record<number, BuildingStore> = {};
    let buildingsOrder: number[] = [];
    let error: APIError | undefined = undefined;
    const getObjects = apiService.withErrorHandler(e => { error = e }).createRequest(GetObjectList_RequestTypes);
    const res = await withLoading(self, getObjects(rc.createRequestHandle(), location.Id));
    if (error !== undefined) return;
    if (!res || res.Status !== "success" || !res.Records) return;

    for (const rec of res.Records) {
      const buildingId = +rec.id;
      const bld = rec as unknown as IBuilding;
      bld.id = buildingId;
      buildingsOrder.push(buildingId);
      buildings[buildingId] = createBuildingStore(location.Id, location.Name, bld, mainStore);
    }

    return {
      buildings,
      buildingsOrder,
    }
  };

  const self: ILocationStoreBase = {
    location,
    isLoading: false,
    isActive: false,
    buildings: {},
    buildingsOrder: [],
    activeBuilding: undefined,
    selectedUsers: new Set,

    async setActiveBuilding(buildingId: number) {
      for (const [id, store] of entries(self.buildings)) {
        const active = id === `${buildingId}`;
        await store.setActive(active);
        if (active) runInAction(() => {
          self.activeBuilding = store;
          activeBuildingId = buildingId;
        })
      }
    },

    async setActive(active = true) {
      if (active === self.isActive) return;

      if (!active) {
        await self.setActiveBuilding(-1);
        runInAction(() => {
          self.isActive = false;
        })
        return;
      }

      const info = await load();
      if (!info) return;

      runInAction(() => {
        self.buildings = info.buildings;
        self.buildingsOrder = info.buildingsOrder;
      })

      if (!self.buildingsOrder.length) return;
      const bld = activeBuildingId === undefined ? self.buildingsOrder[0] : activeBuildingId;
      await self.setActiveBuilding(bld)
    },

    get buildingsInOrder() {
      return self.buildingsOrder.map(id => self.buildings[id]);
    },

    toggleUserSelection(userId: number) {
      const sel = new Set([...self.selectedUsers]);
      if (!sel.delete(userId)) sel.add(userId);
      self.selectedUsers = sel;
    },

    deleteBuilding(id: number) {
      return new Promise<boolean>(resolve => {
        const onConfirm = async () => {
          const res = await deleteBuilding(rc.createRequestHandle(), id);
          if (!res) {
            resolve(false);
            return
          }
          runInAction(() => {
            const { [id]: _, ...rest } = self.buildings;
            self.buildings = rest;
            self.buildingsOrder = self.buildingsOrder.filter(v => v !== id);
            if (self.activeBuilding !== undefined) {
              self.activeBuilding = self.buildingsOrder.length ? self.buildingsInOrder[0] : undefined;
            }
          });
          resolve(true);
        };
        store_()?.dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, { onConfirm })
        );
      })
    },

    async addBuilding(name: string) {
      const res = await addBuilding(rc.createRequestHandle(), self.location.Id, name);
      if (!res) return false;
      const buildingID = +res.Record;

      const bld: IBuilding = {
        id: buildingID,
        name,
        location_id: `${self.location.Id}`,
        meta: { ElectricScheme: {} },
        ord: self.buildingsOrder.length // oct: чтобы добавлялось в конец
      };

      runInAction(() => {
        self.buildingsOrder.push(buildingID);
        self.buildings = { ...self.buildings, [buildingID]: createBuildingStore(self.location.Id, self.location.Name, bld, mainStore) };
      })
      return true;
    },

    showAddBuildingModal() {
      store_()?.dispatch(
        show(MODALS_ENUM.ADD_BUILDING_MOBX)
      );
    },

    reorderBuildings(newOrder: number[]): boolean {
      if (newOrder.length !== self.buildingsOrder.length) return false;
      const newSet = new Set(newOrder);
      if (!self.buildingsOrder.every(id => newSet.has(id))) return false;
      self.buildingsOrder = newOrder;
      return true;
    },

    async getStatistics(period: [Moment | null, Moment | null], isAdmin?: boolean): Promise<HashrateItem[]> {
      const response = await withLoading(self, getStatistics(rc.createRequestHandle(), period[0], period[1], self.location.Id, isAdmin));
      if (response && response.Status === "success") {
        return response.Record;
      }
      return [];
    },

    showEditLocationModal() {
      store_()?.dispatch(show(MODALS_ENUM.ADD_LOCAION_MOBX, {
        location: self.location,
      }));
    },

    async edit(data: ILocationStringsData) {
      const r = await edit(rc.createRequestHandle(), self.location.Id, data);
      //dbg.log("AFTER_EDIT_API", !!r, data);
      if (!r) return false;
      runInAction(() => {
        self.location = { ...self.location, ...data, Updated: moment().unix() };
      });
      return true;
    }
  };

  return makeObservable(self, {
    location: observable,
    isLoading: observable,
    isActive: observable,
    buildings: observable,
    selectedUsers: observable,
    activeBuilding: observable,
    buildingsOrder: observable,

    buildingsInOrder: computed,

    setActive: action,
    setActiveBuilding: action,
    toggleUserSelection: action,
    deleteBuilding: action,
    addBuilding: action,
    showAddBuildingModal: action,
    reorderBuildings: action,
    getStatistics: action,
    showEditLocationModal: action,
    edit: action,
  });
};

export const createLocationStore = (location: ILocation3, mainStore: IMainLocationsStore) => createStore((rc) => createLocationStore_(rc, location, mainStore));